import { useEffect } from 'react';
import { reaction } from 'mobx';
import { t } from 'i18next';
import { getParticipantDisplayName } from 'features/ParticipantItem';
import { selectRoom, selectRoomMetadata } from 'state/selectors';
import { roomInfoStore } from 'state/store';
import { findParticipantByUserId } from 'useCase/moderation/utils/findParticipantByUserId';
import { parseModeratorsFromMetadata } from 'useCase/moderation/utils/parseModerators';
import {
    consumeSkipWebinarModeNotificationForLocalInitiator,
    consumeSuppressWebinarEnableMetadataNotification,
    showWebinarModeSnackbar,
} from 'useCase/webinar/webinarModeSnackbar';

export {
    skipWebinarModeNotificationForLocalInitiator,
    suppressNextWebinarEnableMetadataNotification,
} from 'useCase/webinar/webinarModeSnackbar';

const trackWebinarEnabled = (): boolean | undefined => roomInfoStore.metadata?.webinarMode?.enabled;

const resolveWebinarActorName = (): string => {
    const unnamed = t('web_call_unnamed_user');
    const room = selectRoom();
    const metadata = selectRoomMetadata();

    if (room && metadata?.mainModeratorUserId != null) {
        const moderator = findParticipantByUserId(room, String(metadata.mainModeratorUserId));
        if (moderator) {
            return getParticipantDisplayName(moderator, unnamed);
        }
    }

    if (room) {
        for (const moderator of parseModeratorsFromMetadata(metadata)) {
            if (moderator.userId) {
                const participant = findParticipantByUserId(room, moderator.userId);
                if (participant) {
                    return getParticipantDisplayName(participant, unnamed);
                }
            }
        }
    }

    return unnamed;
};

/** Snackbar fallback по metadata, если data channel event не дошёл (iOS/Android — основной источник START/END_WEBINAR). */
export const useWebinarModeNotifications = () => {
    useEffect(
        () =>
            reaction(trackWebinarEnabled, (enabled, prevEnabled) => {
                // undefined: в metadata ещё нет webinarMode
                if (typeof enabled !== 'boolean') return;

                const wasOn = prevEnabled === true;
                const isOn = enabled === true;

                // undefined/false → false: первый снимок «вебинар выключен», не выключение
                if (!isOn && !wasOn) return;

                // undefined → true: join в уже идущий вебинар, не mid-call toggle
                if (isOn && !wasOn && typeof prevEnabled !== 'boolean') {
                    if (consumeSuppressWebinarEnableMetadataNotification()) return;
                }

                if (consumeSkipWebinarModeNotificationForLocalInitiator()) return;

                showWebinarModeSnackbar(isOn, resolveWebinarActorName());
            }),
        [],
    );
};
